/**
 * The connect shelf: pick a module for a connection point, point-first.
 *
 * The left-panel flow is module-first (choose a module, then tap a point);
 * the shelf inverts it - long-press a free connection point and choose from
 * what can go there, previewed in place. `freeConnectionPoints` is the
 * server's broadcast of every unused connection point, which exists so the
 * markers can render before anything is selected.
 */
import type { ConnectShelfSlice, ConnectShelfState, ViewerSliceCreator } from "./types";

/** Modules connected via the shelf, newest first, surviving reloads. */
const RECENT_STORAGE_KEY = "connect-shelf-recent";
const RECENT_LIMIT = 8;

function readRecentModules(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    // Storage denied (private mode) or corrupt: recency is a nicety.
    return [];
  }
}

export const connectShelfInitialState: ConnectShelfState = {
  freeConnectionPoints: [],
  connectShelfTarget: null,
  connectModeActive: false,
  recentConnectModules: readRecentModules(),
  connectSnap: null,
  dimensionHint: null,
};

export const createConnectShelfSlice: ViewerSliceCreator<ConnectShelfSlice> = (set) => ({
  ...connectShelfInitialState,

  setConnectionPoints: (msg) =>
    set((state) => {
      state.freeConnectionPoints = msg.points;
      // The point the shelf is working on may just have been consumed by the
      // connect (or its instance deleted); the shelf must not keep offering
      // a target that no longer exists.
      if (
        state.connectShelfTarget &&
        !msg.points.some(
          (point) =>
            point.instance_id === state.connectShelfTarget!.instance_id &&
            point.link_name === state.connectShelfTarget!.link_name
        )
      ) {
        state.connectShelfTarget = null;
      }
    }),

  openConnectShelf: (target) =>
    set((state) => {
      state.connectShelfTarget = target;
      // The shelf owns the next tap; an armed placement module would make
      // that tap also place a module.
      state.pendingPlacementModule = null;
    }),

  setConnectMode: (active) =>
    set((state) => {
      state.connectModeActive = active;
      if (!active) {
        // Leaving the mode also puts its shelf away.
        state.connectShelfTarget = null;
      }
    }),

  closeConnectShelf: () =>
    set((state) => {
      state.connectShelfTarget = null;
    }),

  recordRecentConnectModule: (moduleId) =>
    set((state) => {
      state.recentConnectModules = [
        moduleId,
        ...state.recentConnectModules.filter((id) => id !== moduleId),
      ].slice(0, RECENT_LIMIT);
      try {
        window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(state.recentConnectModules));
      } catch {
        // Same as reading: recency is a nicety, not state worth crashing for.
      }
    }),

  flashConnectSnap: (position) =>
    set((state) => {
      state.connectSnap = { position, seq: (state.connectSnap?.seq ?? 0) + 1 };
    }),

  clearConnectSnap: () =>
    set((state) => {
      state.connectSnap = null;
    }),

  showDimensionHint: (msg) =>
    set((state) => {
      state.dimensionHint = {
        instanceId: msg.instance_id,
        label: msg.label,
        start: msg.start,
        end: msg.end,
        seq: (state.dimensionHint?.seq ?? 0) + 1,
      };
    }),

  clearDimensionHint: () =>
    set((state) => {
      state.dimensionHint = null;
    }),
});
